export class ChemistryVideoError extends Error {
  constructor(code, cause) {
    super(code, cause ? { cause } : undefined);
    this.name = 'ChemistryVideoError';
    this.code = code;
  }
}

export function clamp(value, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}

export function easeOutCubic(value) {
  const progress = clamp(value);
  return 1 - ((1 - progress) ** 3);
}

export function setCanvasFont(ctx, size, weight = 600) {
  ctx.font = `${weight} ${size}px Pretendard, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
}

export function roundedRectPath(ctx, x, y, width, height, radius = 24) {
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, Math.min(radius, width / 2, height / 2));
}

export function fillRoundedRect(ctx, x, y, width, height, radius, fillStyle) {
  roundedRectPath(ctx, x, y, width, height, radius);
  ctx.fillStyle = fillStyle;
  ctx.fill();
}

function getFocusPoint(focus) {
  const x = Number(focus?.x ?? focus?.center_x ?? focus?.centerX);
  const y = Number(focus?.y ?? focus?.center_y ?? focus?.centerY);
  return {
    x: Number.isFinite(x) ? clamp(x) : 0.5,
    y: Number.isFinite(y) ? clamp(y) : 0.5,
  };
}

export function drawCoverImage(ctx, image, x, y, width, height, focus) {
  if (!image?.naturalWidth || !image?.naturalHeight) return false;
  const sourceRatio = image.naturalWidth / image.naturalHeight;
  const targetRatio = width / height;
  let sourceWidth = image.naturalWidth;
  let sourceHeight = image.naturalHeight;
  if (sourceRatio > targetRatio) sourceWidth = image.naturalHeight * targetRatio;
  else sourceHeight = image.naturalWidth / targetRatio;

  const point = getFocusPoint(focus);
  const sourceX = clamp((image.naturalWidth * point.x) - (sourceWidth / 2), 0, image.naturalWidth - sourceWidth);
  const sourceY = clamp((image.naturalHeight * point.y) - (sourceHeight / 2), 0, image.naturalHeight - sourceHeight);
  ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
  return true;
}

export function drawFallbackPortrait(ctx, name, x, y, width, height, accent = '#ff6b8a') {
  const gradient = ctx.createLinearGradient(x, y, x + width, y + height);
  gradient.addColorStop(0, `${accent}55`);
  gradient.addColorStop(1, '#15142b');
  ctx.fillStyle = gradient;
  ctx.fillRect(x, y, width, height);
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  setCanvasFont(ctx, Math.min(width, height) * 0.25, 800);
  ctx.fillText(String(name || '?').trim().slice(0, 1).toUpperCase(), x + (width / 2), y + (height / 2));
}

export function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 3) {
  const value = String(text || '');
  const words = value.includes(' ') ? value.split(/\s+/) : Array.from(value);
  const spacer = value.includes(' ') ? ' ' : '';
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const candidate = current ? `${current}${spacer}${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) current = candidate;
    else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  lines.slice(0, maxLines).forEach((line, index) => {
    const isTruncated = index === maxLines - 1 && lines.length > maxLines;
    ctx.fillText(isTruncated ? `${line}…` : line, x, y + (index * lineHeight));
  });
  return Math.min(lines.length, maxLines) * lineHeight;
}

export function clearCanvas(canvas) {
  const ctx = canvas?.getContext?.('2d');
  if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
}
